import { useEffect } from "react";

import { env, siteOrigin } from "@/env";

export type SeoProps = {
  title: string;
  description: string;

  path: string;

  bare?: boolean;

  noindex?: boolean;

  image?: string;
  imageAlt?: string;
  type?: "website" | "article" | "profile";

  jsonLd?: Record<string, unknown>;
};

export const SHARE_CARD = import.meta.env.BSMEET_OG_IMAGE;
const JSON_LD_ID = "seo-page-jsonld";

function absolute(pathOrUrl: string): string {
  if (/^https?:\/\//u.test(pathOrUrl)) return pathOrUrl;
  return `${siteOrigin()}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
}

function upsert<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  selector: string,
  attributes: Record<string, string>,
): HTMLElementTagNameMap[K] {
  let element = document.head.querySelector<HTMLElementTagNameMap[K]>(selector);
  if (element === null) {
    element = document.createElement(tag);
    document.head.append(element);
  }
  for (const [name, value] of Object.entries(attributes)) {
    if (element.getAttribute(name) !== value) element.setAttribute(name, value);
  }
  return element;
}

function setMeta(attribute: "name" | "property", key: string, content: string): void {
  upsert("meta", `meta[${attribute}="${key}"]`, { [attribute]: key, content });
}

function setLink(rel: string, href: string): void {
  upsert("link", `link[rel="${rel}"]`, { rel, href });
}

export function Seo({
  title,
  description,
  path,
  bare = false,
  noindex = false,
  image = SHARE_CARD,
  imageAlt,
  type = "website",
  jsonLd,
}: SeoProps) {
  const fullTitle = bare ? title : `${title} · ${env.appName}`;
  const url = absolute(path);
  const imageUrl = absolute(image);
  const alt = imageAlt ?? `${env.appName}: ${description}`;
  const robots = noindex
    ? "noindex, nofollow, noarchive"
    : "index, follow, max-image-preview:large";
  const serialized = jsonLd === undefined ? null : JSON.stringify(jsonLd);

  useEffect(() => {
    document.title = fullTitle;

    setMeta("name", "description", description);
    setMeta("name", "robots", robots);
    setMeta("name", "googlebot", robots);
    setLink("canonical", url);

    setMeta("property", "og:type", type);
    setMeta("property", "og:site_name", env.appName);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", imageUrl);
    setMeta("property", "og:image:alt", alt);

    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", imageUrl);
    setMeta("name", "twitter:image:alt", alt);
  }, [fullTitle, description, url, imageUrl, alt, robots, type]);

  useEffect(() => {
    const existing = document.head.querySelector<HTMLScriptElement>(`script#${JSON_LD_ID}`);
    if (serialized === null) {
      existing?.remove();
      return undefined;
    }
    const script = existing ?? document.createElement("script");
    script.id = JSON_LD_ID;
    script.type = "application/ld+json";
    script.textContent = serialized;
    if (existing === null) document.head.append(script);
    return () => {
      script.remove();
    };
  }, [serialized]);

  return null;
}
